/*LIBRARY*/
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormatRupiah } from "@arismun/format-rupiah";
import { useDispatch } from "react-redux";
import Pagination from "@mui/material/Pagination";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
} from "@mui/material";

/*COMPONENT*/
import Modal from "../common_modal/Modal.jsx";
import { Dropdown } from "../reuseable";
import UploadImage from "../custom_cell/UploadImage";
import UploadFile from "../custom_cell/UploadFile";

import { TABLE_NESTED_CELL } from "../custom_cell";

/*REDUX*/
import { set_value_properties } from "../../App/actions/propertiesActions";
import { set_value_genangan } from "../../App/actions/genangan_actions";
import {
  deleteFeature,
  editProperties,
  editPropertiesV2,
  set_value_layer,
} from "../../App/actions/layerActions";

/*PICTURE*/
import icon_type_date from "../../Assets/svg/icon_type_date.svg";
import icon_download from "../../Assets/svg/icon_import.svg";

/*FUNCTION*/
import useLanguage from "../common_hooks/useLanguage";
import { dot_every_3_digits } from "../../App/validation/format_number";
import { convertUnit } from "../../App/validation/geometry_to_area_or_length";
import format_date_formal from "../../App/validation/format_date_formal";
import { is_valid_url } from "../../App/validation/is_valid_url";
import { getFeatureByKey } from "../../App/reduxHelper/layer";
import is_not_empty from "../../App/validation/is_not_empty";

/*DATA*/
import dict from "../../Data/dict.json";

/*CONST*/
const WAIT_INTERVAL = 1000;

/**
 * Keterangan:
 *
 * PopupValue ini merupakan Component popup yang terdiri Title dan value, komponen ini TIDAK ADA fitur editor. Component ini akan muncul ketika component POPUP_EDITOR berjalan.
 */
const PopupValue = (params) => {
  const language = useState("ina")[0];
  const { data, properties_selected } = params;
  const { value, unit } = convertUnit(
    properties_selected?.["_geometry"]?.value,
    data?.unit,
  );

  let final_render_value = properties_selected
    ? properties_selected[data?.key]
    : undefined;
  if (!is_not_empty(final_render_value)) {
    final_render_value = "-";
  }

  return (
    <div className="valueNewPopup">
      {
        <>
          <p>{data?.name}</p>
          <div className="border_grey rounded_5 padding_10">
            {data?.type === "text" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "textarea" ? (
              <h3 className="popup_textarea">{final_render_value}</h3>
            ) : data?.type === "number" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "phone_number" ? (
              <h3>+{final_render_value}</h3>
            ) : data?.type === "currency" ? (
              <h3>
                <FormatRupiah value={final_render_value} />
              </h3>
            ) : data?.type === "year" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "date" ? (
              <h3>{format_date_formal(final_render_value)}</h3>
            ) : data?.type === "time" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "selection" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "radio_button" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "checklist" ? (
              <h3>{final_render_value?.split(" ")}</h3>
            ) : data?.type === "image" ? (
              <img
                src={properties_selected ? properties_selected[data.key] : ""} // Ensure properties_selected exists
                alt={data?.name}
                className="photo_popup"
              />
            ) : data?.type === "document" ? (
              <a
                href={
                  final_render_value && final_render_value.includes("https://")
                    ? final_render_value
                    : `https://${final_render_value}`
                }
                className={`noselect rounded_5 padding_5 ${
                  final_render_value ? "bg_blue pointer" : "bg_grey"
                }`}
                style={{
                  color: final_render_value ? "white" : "black",
                }}
              >
                {final_render_value
                  ? dict?.["Download"]?.[language]
                  : dict?.["No document"]?.[language]}
              </a>
            ) : data?.type === "link" ? (
              <a
                href={
                  final_render_value && final_render_value.includes("https://")
                    ? final_render_value
                    : `https://${final_render_value}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {final_render_value}
              </a>
            ) : data?.type === "auto_address" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "auto_address_poi" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "table" ? (
              <h3>{final_render_value}</h3>
            ) : data?.type === "calculate_dimension" ? (
              <h3>{`${dot_every_3_digits(value)} ${unit}`}</h3>
            ) : (
              <h3>{final_render_value}</h3>
            )}
          </div>
        </>
      }
    </div>
  );
};

// Tipe yang di-render sebagai text.
const render_like_text = ["text", "phone_number"];
// Tipe yang di-render sebagai number.
const render_like_number = ["number", "currency", "year"];

// Tipe yang tidak bisa diubah (uneditable).
const render_uneditable = [
  "kelurahan",
  "kecamatan",
  "kota",
  "provinsi",
  "auto_address",
  "auto_address_poi",
  "counting_poi",
  "counting_custom",
  "simple_referring",
  "2d_referring",
  "math_operators",
  "conditional_statement",
  "auto_generate_user_name",
  "last_edited",
  "edited_by",
  "longitude",
  "latitude",
];

/**
 * Keterangan:
 *
 * PopupFormEdit ini merupakan Component popup yang terdiri Title dan value, namun untuk kasus tertentu, value dapat diubah melalui komponen ini. Component ini akan muncul ketika component POPUP_EDITOR berjalan.
 */
const PopupFormEdit = ({
  data,
  properties_selected, //properties
  is_mode_edit,
  on_change_callback,
  index,
  page,
  geo_layer_selected,
  geo_layer_id,
}) => {
  const [timer, setTimer] = useState(null);
  const [textfield, set_textfield] = useState(
    properties_selected ? properties_selected[data?.key] : "",
  ); // Initialize with "" if properties_selected is undefined
  const language = useState(localStorage?.language || "ina")[0];

  // This useEffect correctly syncs the local textfield state
  // when the properties from the parent component change.
  // It also handles cases where properties_selected becomes undefined.
  useEffect(() => {
    // If properties_selected is undefined or null, reset the textfield to an empty string.
    // Otherwise, set it to the specific property value.
    set_textfield(
      properties_selected ? properties_selected[data?.key] || "" : "",
    );
  }, [properties_selected, data?.key]);

  const handle_checkbox =
    ({ value, field_key }) =>
    () => {
      // Memecah textfield menjadi array berdasarkan newline.
      const splitted_list = textfield?.split("\n") || [];

      // Mencari index dari nilai checkbox.
      const currentIndex = splitted_list?.indexOf(value);

      // Membuat salinan dari list yang sudah di-split.
      const newChecked = structuredClone(splitted_list);
      if (currentIndex === -1) {
        // Jika belum dipilih, tambahkan.
        newChecked.push(value);
      } else {
        // Jika sudah dipilih, hapus dari list.
        newChecked.splice(currentIndex, 1);
      }
      set_textfield(newChecked.join("\n"));
      on_change_callback(field_key, newChecked.join("\n"));
    };

  const handle_stright_forward = (value, field_key) => {
    set_textfield(value);
    on_change_callback(field_key, value);
  };

  const handle_on_change_timer = (field_key, value) => {
    set_textfield(value);
    clearTimeout(timer);
    const newTimer = setTimeout(() => {
      // Set nilai setelah interval tertentu (debounce).
      on_change_callback(field_key, value);
    }, WAIT_INTERVAL);
    setTimer(newTimer);
  };

  // Render konten sesuai dengan tipe field.
  let content = <></>;

  const array_templates =
    data?.array_templates?.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.name === item.name),
    ) || [];

  if (render_like_text?.includes(data?.type)) {
    content = (
      <input
        className={`w_full ${is_mode_edit ? "bg_white" : "bg_lightGrey"}`}
        key={data?.key}
        type="text"
        value={textfield || ""}
        onChange={(e) => {
          handle_on_change_timer(data?.key, e?.target?.value);
        }}
        disabled={!is_mode_edit} // Disabled jika mode edit mati.
      />
    );
  } else if (render_like_number?.includes(data?.type)) {
    content = (
      <input
        className={`w_full ${is_mode_edit ? "bg_white" : "bg_lightGrey"}`}
        key={data?.key}
        type="number"
        value={textfield || ""}
        onChange={(e) => handle_on_change_timer(data?.key, e?.target?.value)}
        disabled={!is_mode_edit}
      />
    );
  } else if (data?.type === "textarea") {
    content = (
      <textarea
        className={`w_full ${
          is_mode_edit ? "bg_white" : "bg_lightGrey"
        } min_h_200 max_h_500`}
        key={data?.key}
        type="text"
        value={textfield || ""}
        // Panggil debounce onChange.
        onChange={(e) => handle_on_change_timer(data?.key, e?.target?.value)}
        disabled={!is_mode_edit}
      />
    );
  } else if (data?.type === "range") {
    content = (
      <div>
        <label className="flex justify_end">{textfield || 0}</label>
        <input
          className="w_full"
          key={data?.key}
          type="range"
          value={textfield || ""}
          // Panggil debounce onChange.
          onChange={(e) => handle_on_change_timer(data?.key, e?.target?.value)}
          disabled={!is_mode_edit}
          min={data?.min || 0}
          max={data?.max || 100}
          step={data?.step || 1}
        />
      </div>
    );
  } else if (data?.type === "date") {
    const converted_date = textfield ? format_date_formal(textfield) : "";
    content = (
      <main className="center_perfect w_full">
        <div
          className={`w_full h_full flex justify_between border_grey rounded_5 padding_5 ${
            is_mode_edit ? "bg_white" : "bg_lightGrey"
          }`}
        >
          <span title="DD-MM-YYYY">{converted_date}</span>
          {is_mode_edit && (
            <button className="datepicker-native-button bg_grey rounded_5 pointer button_inactive">
              <img alt="date" src={icon_type_date} className="w_15 h_15" />
              <input
                className="datepicker-native-button-input"
                value={textfield || ""}
                type="date"
                onChange={(e) =>
                  handle_stright_forward(e.target.value, data?.key)
                }
                disabled={!is_mode_edit}
              />
            </button>
          )}
        </div>
      </main>
    );
  } else if (data?.type === "time") {
    content = (
      <input
        className={is_mode_edit ? "bg_white" : "bg_lightGrey"}
        id="time_input"
        type="time"
        value={textfield || ""}
        // Set langsung tanpa debounce.
        onChange={(e) => handle_stright_forward(e.target.value, data?.key)}
        disabled={!is_mode_edit}
      />
    );
  } else if (data?.type === "checklist") {
    content = (
      <List style={{ paddingTop: 0, paddingBottom: 0, margin: 0 }}>
        {array_templates?.map((item) => (
          <ListItem
            key={item?._id}
            value={item?._id}
            style={{ paddingTop: 0, paddingBottom: 0, margin: 0, height: 30 }}
            secondaryAction={
              <Checkbox
                disabled={!is_mode_edit}
                // Panggil handle_checkbox saat checklist berubah.
                onChange={handle_checkbox({
                  value: item?.name,
                  field_key: data?.key,
                })}
                // Cek apakah nilai ada di checklist.
                checked={textfield?.includes(item?.name) || false}
              />
            }
          >
            {item?.name}
          </ListItem>
        ))}
      </List>
    );
  } else if (data?.type === "radio_button") {
    content = (
      <FormControl>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          defaultValue="female"
          name="radio-buttons-group"
          // Set nilai langsung tanpa debounce.
          onChange={(e, value) => handle_stright_forward(value, data?.key)}
          value={textfield || ""}
        >
          <div className="flex flex_col">
            {array_templates?.map((item) => (
              <FormControlLabel
                disabled={!is_mode_edit}
                key={item?._id}
                value={item?.name}
                control={
                  <Radio
                    style={{
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                  />
                }
                label={
                  <p className="text_black font_12 text_grey">{item?.name}</p>
                }
                style={{
                  paddingTop: 0,
                  paddingBottom: 0,
                  margin: 0,
                  wordBreak: "break-word",
                }}
              />
            ))}
            {!array_templates?.some(
              (template) => template?.name === textfield,
            ) &&
              is_not_empty(textfield) && (
                <FormControlLabel
                  disabled={!is_mode_edit}
                  value={textfield || ""}
                  control={
                    <Radio
                      style={{
                        paddingTop: 0,
                        paddingBottom: 0,
                      }}
                    />
                  }
                  label={
                    <p className="text_black font_12 text_grey">{textfield}</p>
                  }
                  style={{
                    paddingTop: 0,
                    paddingBottom: 0,
                    margin: 0,
                    wordBreak: "break-word",
                  }}
                />
              )}
          </div>
        </RadioGroup>
      </FormControl>
    );
  } else if (data?.type === "selection") {
    const parent_array_templates_key = data?.parent_array_templates_key;

    let filtered_options = array_templates;

    if (parent_array_templates_key) {
      const keyword = properties_selected?.[parent_array_templates_key];
      filtered_options = array_templates.filter(
        (option) => option.parent === keyword,
      );
    }

    const merged_option = filtered_options?.map((item) => (
      <option key={item?.name} value={item?.name}>
        {<p className="text_black font_12">{item?.name}</p>}
      </option>
    ));

    // ini buat nambahin opsi textfield kalo emang gaada di list
    if (
      !filtered_options?.some((template) => template?.name === textfield) &&
      is_not_empty(textfield, [""])
    ) {
      merged_option.push(
        <option key={textfield} value={textfield || ""}>
          {textfield}
        </option>,
      );
    }
    if (is_mode_edit) {
      // content = (
      //   <Dropdown
      //     placeholder={dict["Select"][language]}
      //     filter_on
      //     is_enable_empty
      //     value={textfield || ""}
      //     // Set nilai langsung saat berubah.
      //     onChange={(value) => {
      //       handle_stright_forward(value, data?.key);
      //     }}
      //   >
      //     {merged_option}
      //   </Dropdown>
      // );

      content = (
        <Dropdown
          height="200px"
          placeholder={dict["Select"][language]}
          filter_on
          is_enable_empty
          value={textfield || ""}
          onChange={(value) => {
            handle_stright_forward(value, data?.key);
          }}
        >
          {merged_option}
        </Dropdown>
      );
    } else {
      content = <div>{textfield ? textfield : "-"}</div>;
    }
  } else if (data?.type === "image") {
    content = (
      <div>
        <img
          src={textfield}
          style={{
            width: "100%",
            objectFit: "cover",
          }}
          alt=""
        />
        {is_mode_edit && (
          <div className="flex justify_end">
            <UploadImage
              imageUrl={textfield}
              geo_layer_id={geo_layer_id}
              feature_key={properties_selected?.key}
              properties_key={data?.key}
              set_textfield={handle_stright_forward} // Panggil callback upload.
            />
          </div>
        )}
      </div>
    );
  } else if (data?.type === "document") {
    // Mengambil nama file dari URL.
    const fileNameOnServer = textfield?.split("/")?.at(-1);

    // Mengambil nama asli file.
    const arrayName = fileNameOnServer?.split("_%25");

    // Mendapatkan ekstensi file.
    const extension = textfield?.split(".").at(-1);
    const originalName =
      arrayName?.at(0)?.split("_")?.at(1) === "doc"
        ? arrayName?.at(1)?.replaceAll("%20", " ") +
          (extension ? `.${extension}` : "")
        : "";
    content = (
      <div>
        {textfield ? (
          <div title={originalName}>
            <p>{originalName}</p>
          </div>
        ) : (
          <div
            style={{
              fontSize: "10px",
              width: 80,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginLeft: 5,
            }}
          >
            {dict["No document"][language]}
          </div>
        )}
        <div className="flex justify_between">
          <div className="flex justify_end">
            {is_mode_edit && (
              <UploadFile
                imageUrl={textfield}
                geo_layer_id={geo_layer_id}
                feature_key={properties_selected?.key}
                properties_key={data?.key}
                // Panggil callback upload file.
                set_textfield={handle_stright_forward}
              />
            )}
          </div>
          <a
            className="hover_darkGreen hover_bigger pointer"
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: "10px",
              width: 15,
              height: 15,
              backgroundColor: "#e6eef3",
              padding: "5px",
              border: "1px solid #9ca8bb",
              borderRadius: "99px",
            }}
            target="_blank"
            href={textfield || "https://mapid.io"}
            rel="noreferrer"
          >
            <img
              alt="download"
              src={icon_download}
              className="h_15 w_15 rounded"
            />
          </a>
        </div>
      </div>
    );
  } else if (data?.type === "link") {
    let href = textfield;
    if (is_valid_url(textfield)) {
      if (textfield?.includes("https://") || textfield?.includes("http://")) {
        // do nothing
      } else {
        // Tambahkan https jika URL tidak valid.
        href = `https://${textfield}`;
      }
    }
    content = is_mode_edit ? (
      <input
        className={`w_full`}
        key={data?.key}
        type="text"
        value={textfield || ""}
        // Panggil debounce onChange.
        onChange={(e) => {
          handle_on_change_timer(data?.key, e.target.value);
        }}
        disabled={!is_mode_edit}
      />
    ) : (
      <a className="url" rel="noopener noreferrer" target="_blank" href={href}>
        {textfield || "-"}
      </a>
    );
  } else if (render_uneditable.includes(data?.type)) {
    // Render uneditable field.
    if (data?.type === "last_edited") {
      let formattedDate = "-";

      if (textfield) {
        const date = new Date(textfield);

        // --- FIX: Check if date is valid before formatting ---
        if (!isNaN(date.getTime())) {
          try {
            let formatter = new Intl.DateTimeFormat("id-ID", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
              timeZone: "Asia/Jakarta",
            });
            formattedDate = formatter.format(date);
          } catch (error) {
            console.error("Date formatting error:", error);
            formattedDate = "Invalid Date";
          }
        }
      }

      content = <div>{formattedDate}</div>;
    } else {
      content = <div>{textfield ? textfield : "-"}</div>;
    }
  } else if (data?.type === "calculate_dimension") {
    // Konversi dimensi.
    const { unit } = convertUnit(textfield["_geometry"]?.value, data.unit);
    // Render hasil konversi.
    content = <div>{`${textfield} ${unit}`}</div>;
  } else if (data?.type === "table") {
    const feature = getFeatureByKey({
      geo_layer: geo_layer_selected,
      feature_key: properties_selected?.["key"],
    });
    content = (
      <TABLE_NESTED_CELL
        layer_id={geo_layer_id}
        row={feature}
        column={data}
        is_from_first_gen_table={true}
      />
    );
  } else if (data?.type === "template_literal") {
    const { template_literal_text } = data;
    content = (
      <div>
        {textfield
          ? textfield
          : `${template_literal_text?.first || ""} - ${template_literal_text?.last || ""}`}
      </div>
    );
  } else {
    content = <div>{textfield ? textfield : "-"}</div>;
  }
  return (
    <div className="flex w_full bold_border_grey rounded_5 padding_10">
      <p>{index + 1}.</p>
      <div className="w_full">
        <section className="flex justify_between">
          <p className="bold font_16 text_blue">{data?.name}</p>
          <p className="font_16 text_orange underline">
            {data?.type
              ?.split("_")
              .map((word) => word?.[0].toUpperCase() + word?.slice(1))
              .join(" ")}
          </p>
        </section>
        <p className="text_inferior sub_color margin_bottom">
          {data?.description}
        </p>
        {content}
      </div>
    </div>
  );
};

/**
 * Keterangan
 *
 * POPUP_EDITOR merupakan suatu component popup yang akan muncul ketika suatu geometry ditekan/diklik. Component ini memiliki 2 children. PopupFormEdit (bisa diedit) dan PopupValue (tidak bisa diedit). PopupFormEdit akan dirender hanya ketika mode fullscreen saja sehingga secara default akan merender PopupValue.
 */
const POPUP_EDITOR = ({
  longitude_selected,
  latitude_selected,
  fields_selected,
  properties_selected,
  type_2_selected,
  feature_key_selected,
  genangan_cm_selected,
  geo_layer_selected,
  is_editable,
}) => {
  const auth = useSelector((state) => state.auth);
  const username = auth.user?.name?.trim() || "";
  const domain = window.location.host;

  const dispatch = useDispatch();
  const { language } = useLanguage();
  const [is_mode_edit, set_is_mode_edit] = useState(false);
  // Initialize data_popup with properties_selected, defaulting to an empty object if undefined.
  const [data_popup, set_data_popup] = useState(properties_selected || {});
  const [rendered_input_field, set_rendered_input_field] = useState([]);
  const [page, set_page] = useState(1);
  const [modal_delete_feature, set_modal_delete_feature] = useState(false);

  // DSDA
  const allowedDsdaDomainAccess = [
    "localhost:3003",
    "dsdajakarta.mapid.io",
    "dsdajakarta-alpha.mapid.io",
    "dsdajakarta-beta.mapid.io",
  ].includes(domain);

  const restrictedDsdaAccess =
    allowedDsdaDomainAccess &&
    !["dsdajakarta", "pusdatin_SDA"].includes(username);

  // Effect 1: Sync incoming prop with internal state.
  // This runs ONLY when the main `properties_selected` from Redux changes.
  useEffect(() => {
    // If properties_selected is undefined or null, reset data_popup to an empty object.
    // This ensures the component clears out old data.
    set_data_popup(properties_selected || {});
  }, [properties_selected]);

  // Effect 2: Re-render the attribute list when the internal data changes or view mode changes.
  // This runs after Effect 1 finishes, ensuring `data_popup` is up-to-date.
  useEffect(() => {
    const fieldsMap = render_list_attributes() || [];
    set_rendered_input_field(fieldsMap);
    // eslint-disable-next-line
  }, [data_popup, is_mode_edit, page, fields_selected]);

  const modal_edit_feature_properties = useSelector(
    (state) => state.layer.modal_edit_feature_properties,
  );

  const popup_dom = useSelector((state) => state.layer.popup_dom);

  const toggle_delete_feature = (value) => {
    if (value === true || value === false) {
      set_modal_delete_feature(value);
    } else {
      set_modal_delete_feature(!modal_delete_feature);
    }
  };

  const close_modal_fullscreen = () => {
    dispatch(
      set_value_layer({
        key: "modal_edit_feature_properties",
        value: false,
      }),
    ); // Tutup modal fullscreen edit feature.
  };

  const on_change_page = (event, value) => {
    set_page(value);
  };

  const toggle_mode_edit = () => {
    set_is_mode_edit(!is_mode_edit);
  };

  // This function provides optimistic UI updates by modifying the local state first.
  const on_change_callback = (field_key, value) => {
    let new_data = structuredClone(data_popup);
    new_data[field_key] = value;
    // Dispatch the update to Redux/backend.
    submit(geo_layer_selected?._id, properties_selected?.key, field_key, value);
    // Update the local state immediately for a responsive UI.
    set_data_popup(new_data);
  };

  const submit = (
    geo_layer_id,
    feature_key_selected,
    properties_key,
    properties_value,
  ) => {
    const body = {
      geo_layer_id,
      feature_key: feature_key_selected,
      properties_key,
      properties_value,
    };
    if (geo_layer_selected?.architecture === "v2") {
      dispatch(editPropertiesV2(body));
    } else if (geo_layer_selected?.architecture === "api") {
      // do nothing for now.
    } else {
      dispatch(editProperties(body));
    }
  };

  // This function now correctly reads the up-to-date `data_popup` state.
  const render_list_attributes = () => {
    let fieldsMap = [];
    // Ensure fields_selected is an array before iterating
    const current_fields_selected =
      fields_selected?.filter((item) => !item?.parent_uuid) || [];

    if (modal_edit_feature_properties) {
      let index = 0;
      let subFieldMap = [];
      let section_counter = 0;
      for (const data of current_fields_selected) {
        if (data.isStyle !== true && data?.type !== "section") {
          const element = (
            <div key={index} className="marginTop_5 marginBottom_5">
              <PopupFormEdit
                index={index}
                data={data}
                properties_selected={data_popup}
                is_mode_edit={is_mode_edit}
                on_change_callback={on_change_callback}
                page={section_counter}
                geo_layer_id={geo_layer_selected?._id}
                geo_layer_selected={
                  data?.type === "table" ? geo_layer_selected : undefined
                }
              />
            </div>
          );
          index++;
          subFieldMap.push(element);
        } else if (data?.type === "section") {
          if (subFieldMap?.length > 0) {
            fieldsMap.push(subFieldMap); // Tambahkan subFieldMap ke fieldsMap.
          }
          const element = (
            <div
              key={`section-${index}`}
              className="marginTop_5 marginBottom_5"
            >
              <label className="font_30 bold">{`Section: ${data?.name}`}</label>
            </div>
          );
          subFieldMap = [element];
          section_counter++;
        }
      }
      if (subFieldMap?.length > 0) {
        fieldsMap.push(subFieldMap); // Tambahkan subFieldMap terakhir jika ada.
      }
    } else {
      let index = 0;
      current_fields_selected.forEach((data) => {
        const element = (
          <div key={index}>
            {data.isStyle !== true && (
              <PopupValue
                data={data}
                properties_selected={data_popup}
                feature_key_selected={feature_key_selected}
              />
            )}
          </div>
        );
        index++;
        fieldsMap.push(element);
      });
    }
    return fieldsMap;
  };

  const on_get_genangan = () => {
    dispatch(
      set_value_properties({
        key: "modal_flood_new",
        value: true,
      }),
    );
    dispatch(
      set_value_genangan({
        key: "geojson_flood_in_progress",
        value: {
          type: "FeatureCollection",
          features: [],
        },
      }),
    );

    dispatch(
      set_value_genangan({
        key: "current_dem",
        value: 0,
      }),
    );

    dispatch(
      set_value_genangan({
        key: "current_calc",
        value: 0,
      }),
    );

    dispatch(
      set_value_layer({
        key: "modal_edit_feature_properties",
        value: false,
      }),
    );
  };

  const toggle_share = () => {
    dispatch(
      set_value_properties({
        key: "feature_will_be_shared_key",
        value: feature_key_selected,
      }),
    );
    dispatch(
      set_value_properties({
        key: "modal_share",
        value: true,
      }),
    );
  };

  const handle_delete_feature = () => {
    const content = {
      feature_key: feature_key_selected,
      geo_layer_id: geo_layer_selected?._id,
    };
    dispatch(deleteFeature(content)).then(() => {
      toggle_delete_feature(false);
      close_modal_fullscreen();
      if (popup_dom) {
        popup_dom.remove();
        dispatch(
          set_value_layer({
            key: "popup_dom",
            value: null,
          }),
        );
      }
    });
  };

  const handleSaveFeature = () => {
    close_modal_fullscreen();
  };

  const modal_delete_feature_content = modal_delete_feature && (
    <Modal
      modalSize="small"
      id="modal"
      isOpen={modal_delete_feature}
      onClose={toggle_delete_feature}
    >
      <div className="box-body">
        <p className="text_bold">
          {dict?.["Are you sure to delete this feature?"]?.[language]}
        </p>
        <br />
        <p className="text_bold">
          {dict?.["This process can't be undone."]?.[language]}
        </p>
        <br />
        <br />
        <br />
        <button
          className="button background_red"
          style={{ marginTop: "1rem" }}
          onClick={handle_delete_feature}
        >
          {dict?.["Delete"]?.[language]}
        </button>
      </div>
    </Modal>
  );

  const modal_fullscreen_content = modal_edit_feature_properties && (
    <Modal
      title="Edit detail"
      modalSize="medium"
      isOpen={modal_edit_feature_properties}
      onClose={close_modal_fullscreen}
    >
      <div className="box-body">
        {is_editable && (
          <header
            className="background_white w_full flex"
            style={{
              justifyContent: "end",
            }}
          >
            <div className="flex gap_10 fixed padding_5 background_white shadow rounded_5">
              {restrictedDsdaAccess ? (
                <></>
              ) : (
                <button
                  onClick={toggle_delete_feature}
                  className="center_perfect pointer hover_bigger button background_red"
                >
                  Delete
                </button>
              )}
              <button
                onClick={toggle_mode_edit}
                className="bg_blue text_white button_inactive rounded_5 padding_5"
              >
                Edit : {is_mode_edit ? "ON" : "OFF"}
              </button>
            </div>
          </header>
        )}
        <main className="marginTop_30 marginRight_10 min_h_500 center_perfect">
          <div className="modal_content flex flex_col gap_10">
            {type_2_selected === "banjir" && (
              <>
                <button
                  className="button margin_bottom background_black"
                  onClick={on_get_genangan}
                >
                  Luas genangan banjir
                </button>
                <button
                  className="button margin_bottom background_black"
                  onClick={toggle_share}
                >
                  Share
                </button>
              </>
            )}
            {/*
              Render the current page of input fields.
              Ensure `rendered_input_field` is an array before accessing by index.
            */}
            {Array.isArray(rendered_input_field) &&
              rendered_input_field?.length > 0 &&
              rendered_input_field?.[page - 1]}
          </div>
        </main>

        <footer className="flex justify_between padding_5 border_top_grey w_full">
          <Pagination
            count={rendered_input_field?.length || 0}
            page={page}
            onChange={on_change_page}
            showFirstButton
            showLastButton
          />

          {allowedDsdaDomainAccess && (
            <div className="flex gap_20">
              <button
                className="button background_black"
                onClick={handleSaveFeature}
              >
                {dict?.["Save"][language]}
              </button>
            </div>
          )}
        </footer>
      </div>
    </Modal>
  );

  return (
    <>
      {modal_fullscreen_content}
      {modal_delete_feature_content}
    </>
  );
};

export default POPUP_EDITOR;
